import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api";
import { catalogProviderName } from "../../shared/catalogPrompt";
import { isProviderId } from "../../shared/providers";

/** What the private receiver knows about a temporary catalog: only records that actually arrived. */
export type ClaimPreview = {
  state: "waiting" | "ready" | "claimed" | "expired";
  provider: string;
  createdAt?: string;
  expiresAt?: string;
  firstSyncedAt?: string | null;
  lastSyncedAt?: string | null;
  totals: { loops: number; sources: number };
  loops: Array<{ id: string; name: string; description: string | null; cadence: string; status: string; kind: string | null; confidence: string | null; source: { name: string | null; provider: string | null } }>;
};

export function providerLabel(provider: string | undefined): string {
  return isProviderId(provider) && provider !== "other" ? catalogProviderName(provider) : "your agent";
}

/** The claim token is the URL fragment of the claim link; it never leaves the browser. */
export function claimTokenFromUrl(claimUrl: string): string {
  try {
    return new URL(claimUrl).hash.replace(/^#/u, "");
  } catch {
    return "";
  }
}

/**
 * Polls the private receiver every three seconds while the page is visible and the catalog is
 * still waiting or ready. Stops on its own once the catalog is claimed or expired.
 */
export function useClaimPreview(token: string, intervalMs = 3_000) {
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      if (!token) throw new ApiError("This claim link is incomplete", 400, "claim_token_missing");
      const result = await api<ClaimPreview>("/api/bootstrap/preview", { method: "POST", body: JSON.stringify({ token }) });
      setPreview(result);
      setError("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "This temporary catalog could not be opened");
    }
  }, [token]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (!document.hidden && preview?.state !== "claimed" && preview?.state !== "expired") void load();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [load, preview?.state, intervalMs]);

  return { preview, error, reload: load };
}
